/**
 * ErrorBoundary — catches render errors in the subtree and shows a branded
 * fallback instead of React's blank white screen. Without this, any
 * uncaught exception during rendering (e.g. an undefined property on a
 * fetched object) takes the whole page down with no recovery path.
 *
 * We only catch RENDER errors here. Async errors inside event handlers
 * still need their own try/catch — those don't bubble through React.
 */
import React from 'react';
import * as Sentry from '@sentry/react';

export default class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Forward to Sentry if configured (Sentry.init in src/index.js bails
    // out silently when REACT_APP_SENTRY_DSN isn't set). Also keep the
    // console log so local dev still sees the trace immediately.
    try {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info?.componentStack } },
      });
    } catch (_) { /* sentry not initialised — fine */ }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render error caught:', error, info);
  }

  handleReload = () => {
    // Reset state first, then reload — gives the user a chance to retry
    // without losing other tabs.
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: '#030303', color: '#EDEDED', fontFamily: "'Manrope', sans-serif" }}
      >
        <div className="max-w-lg w-full text-center">
          <p
            className="font-mono text-[10px] tracking-[0.4em] mb-6 opacity-60"
            style={{ color: '#F97316' }}
          >
            DAVIDMEDIA
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
            Etwas ist schiefgelaufen.
          </h1>
          <p className="text-base mb-8 leading-relaxed" style={{ color: '#888' }}>
            Ein unerwarteter Fehler ist aufgetreten. Wir wurden benachrichtigt und kümmern uns. Du kannst es nochmal versuchen oder zur Startseite zurück.
          </p>

          {this.state.error?.message && (
            <pre
              className="text-left text-xs font-mono p-4 mb-8 rounded-lg overflow-auto max-h-32"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#aaa',
              }}
            >
              {String(this.state.error.message).slice(0, 500)}
            </pre>
          )}

          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
              style={{ background: '#F97316', color: '#000', fontFamily: "'Manrope', sans-serif" }}
            >
              Neu laden
            </button>
            <button
              onClick={this.handleHome}
              className="px-6 py-3 rounded-full font-medium transition-colors"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Zur Startseite
            </button>
          </div>
        </div>
      </div>
    );
  }
}
